import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Camera, ChevronLeft, ChevronRight, Ruler, ScanLine } from 'lucide-react';
import { t, useLanguage, type Language } from '@/app/utils/i18n';
import type { AqlGrowthDashboardPoint, AqlVisionCapture } from '@/app/services/growth-service';

interface GrowthTimelineProps {
  captures: AqlVisionCapture[];
  series: AqlGrowthDashboardPoint[];
  loading?: boolean;
}

interface CaptureCardData {
  capture: AqlVisionCapture;
  metric: AqlGrowthDashboardPoint | null;
}

const sameDay = (a: string, b: string) => a.slice(0, 10) === b.slice(0, 10);

const DATE_LOCALES: Record<Language, string> = {
  pt: 'pt-PT',
  en: 'en-GB',
  fr: 'fr-FR',
  es: 'es-ES',
  de: 'de-DE'
};

const formatDateTime = (value: string, language: Language) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(DATE_LOCALES[language], {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatMm = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : `${value.toFixed(1)} mm`;

const buildCards = (captures: AqlVisionCapture[], series: AqlGrowthDashboardPoint[]): CaptureCardData[] =>
  captures.map((capture) => ({
    capture,
    metric: series.find((point) => sameDay(point.period_start, capture.captured_at)) ?? null
  }));

export function GrowthTimeline({ captures, series, loading }: GrowthTimelineProps) {
  const language = useLanguage();
  const tr = (key: string) => t('vision', key, language);
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const cards = useMemo(() => buildCards(captures, series), [captures, series]);

  const updateScrollState = useCallback(() => {
    const element = timelineRef.current;
    if (!element) return;
    const maxScrollLeft = element.scrollWidth - element.clientWidth;
    setCanScrollLeft(element.scrollLeft > 4);
    setCanScrollRight(maxScrollLeft - element.scrollLeft > 4);
  }, []);

  useEffect(() => {
    const element = timelineRef.current;
    if (!element) return;

    updateScrollState();
    const handleScroll = () => updateScrollState();
    element.addEventListener('scroll', handleScroll, { passive: true });

    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(element);

    return () => {
      element.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [cards.length, updateScrollState]);

  const scrollTimeline = useCallback((direction: 'previous' | 'next') => {
    const element = timelineRef.current;
    if (!element) return;
    const firstCard = element.querySelector<HTMLElement>('[data-timeline-card]');
    const distance = firstCard ? firstCard.offsetWidth + 10 : Math.round(element.clientWidth * 0.7);
    element.scrollBy({
      left: direction === 'next' ? distance : -distance,
      behavior: 'smooth'
    });
    window.setTimeout(updateScrollState, 320);
  }, [updateScrollState]);

  if (loading) {
    return (
      <div className="min-w-0 rounded-2xl border border-border bg-card p-3 shadow-sm">
        <div className="h-5 w-40 animate-pulse rounded bg-muted/70" />
        <div className="mt-3 flex gap-2.5 overflow-x-auto pb-2">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div key={idx} className="h-36 w-40 shrink-0 animate-pulse rounded-xl bg-muted/40" />
          ))}
        </div>
      </div>
    );
  }

  if (!captures.length) {
    return (
      <div className="min-w-0 rounded-2xl border border-border bg-card p-3 shadow-sm">
        <h3 className="text-base font-semibold text-foreground">{tr('visualTimeline')}</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          {tr('noRecentCapturesForSelection')}
        </p>
      </div>
    );
  }

  return (
    <div className="min-w-0 rounded-2xl border border-border bg-card p-3 shadow-sm">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-foreground">{tr('visualTimeline')}</h3>
          <p className="truncate text-xs text-muted-foreground">
            {tr('visualTimelineSubtitle')}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            onClick={() => scrollTimeline('previous')}
            disabled={!canScrollLeft}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-muted/35 text-muted-foreground hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-35"
            aria-label={tr('previousCaptures')}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => scrollTimeline('next')}
            disabled={!canScrollRight}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-muted/35 text-muted-foreground hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-35"
            aria-label={tr('nextCaptures')}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <Camera className="ml-1 h-4 w-4 text-muted-foreground" />
        </div>
      </div>

      <div className="relative min-w-0">
        <div className="pointer-events-none absolute left-6 right-6 top-2 h-px bg-border" />
        <div
          ref={timelineRef}
          className="flex min-w-0 snap-x snap-mandatory gap-2.5 overflow-x-auto scroll-smooth pb-1 pt-4 [scrollbar-width:thin]"
        >
          {cards.map(({ capture, metric }) => (
            <article
              key={capture.id}
              data-timeline-card
              className="relative flex w-40 shrink-0 snap-start flex-col overflow-hidden rounded-xl border border-border bg-muted/30"
            >
              <span className="absolute left-3 top-1 z-10 h-2 w-2 rounded-full border border-primary/60 bg-primary shadow-sm" />
              <div className="relative aspect-[16/10] w-full bg-slate-900">
                {capture.image_url ? (
                  <img
                    src={capture.image_url}
                    alt={`${tr('capture')} ${formatDateTime(capture.captured_at, language)}`}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                    <ScanLine className="h-4 w-4" />
                  </div>
                )}
                <span className="absolute left-1.5 top-1.5 rounded-full border border-white/20 bg-black/50 px-1.5 py-0.5 text-[9px] font-semibold text-white backdrop-blur">
                  {formatDateTime(capture.captured_at, language)}
                </span>
              </div>
              <div className="space-y-1 p-2.5">
                <div className="flex items-center justify-between gap-2 text-[11px]">
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    <Ruler className="h-3 w-3" />
                    {tr('median')}
                  </span>
                  <span className="font-mono font-semibold text-foreground">
                    {formatMm(metric?.median_size ?? capture.median_size ?? null)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 text-[11px]">
                  <span className="text-muted-foreground">{tr('validSample')}</span>
                  <span className="font-mono text-foreground">
                    {metric?.sample_count ?? capture.sample_count ?? '—'}
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
